import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SecureStreamOptions {
  source: "course" | "premium";
  resourceId: string | null | undefined;
  lessonId: string | null | undefined;
  enabled: boolean;
}

interface SecureStreamResult {
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stream-video`;
const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB chunks
const TOKEN_REFRESH_MS = 45 * 1000; // refresh before 60s expiry

// Lightweight stable device fingerprint (per-browser)
function getFingerprint(): string {
  const KEY = "fitawrari_device_fp";
  let fp = localStorage.getItem(KEY);
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem(KEY, fp);
  }
  return fp;
}

async function fetchToken(opts: {
  source: string; resourceId: string; lessonId: string; fingerprint: string;
}): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Not authenticated");

  const res = await fetch(`${FUNCTIONS_BASE}/issue`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${accessToken}`,
    },
    body: JSON.stringify(opts),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed to authorize" }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.token as string;
}

async function fetchChunk(token: string, start: number, end: number): Promise<{
  buffer: ArrayBuffer; totalSize: number; contentType: string;
}> {
  const res = await fetch(`${FUNCTIONS_BASE}/chunk?t=${encodeURIComponent(token)}`, {
    headers: { Range: `bytes=${start}-${end}` },
  });
  if (!res.ok && res.status !== 206 && res.status !== 200) {
    throw new Error(`Chunk fetch failed: ${res.status}`);
  }
  const buffer = await res.arrayBuffer();
  const range = res.headers.get("Content-Range") || "";
  const totalMatch = range.match(/\/(\d+)$/);
  const totalSize = totalMatch ? parseInt(totalMatch[1], 10) : buffer.byteLength;
  const contentType = res.headers.get("Content-Type") || "video/mp4";
  return { buffer, totalSize, contentType };
}

/**
 * Streams an authenticated video as a blob URL using MediaSource.
 * The original file URL is NEVER exposed to the client — only authenticated
 * chunked requests work, defeating IDM and similar download managers.
 */
export const useSecureVideoStream = ({
  source, resourceId, lessonId, enabled,
}: SecureStreamOptions): SecureStreamResult => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled || !resourceId || !lessonId) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;
    let mediaSource: MediaSource | null = null;
    let currentBlobUrl: string | null = null;
    let tokenRefreshTimer: number | null = null;
    let currentToken = "";

    const start = async () => {
      setLoading(true);
      setError(null);
      try {
        const fingerprint = getFingerprint();
        currentToken = await fetchToken({
          source, resourceId, lessonId, fingerprint,
        });

        // Refresh token every 45s while playing
        tokenRefreshTimer = window.setInterval(async () => {
          try {
            currentToken = await fetchToken({
              source, resourceId, lessonId, fingerprint,
            });
          } catch (e) {
            console.warn("[secure-stream] token refresh failed", e);
          }
        }, TOKEN_REFRESH_MS);

        // Probe first chunk to get total size + mime
        const first = await fetchChunk(currentToken, 0, CHUNK_SIZE - 1);
        if (cancelled) return;
        const totalSize = first.totalSize;

        // MediaSource path — only works for fragmented/streamable MP4.
        // Fallback: assemble entire file as a blob (works for any MP4 but uses RAM).
        const mime = 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"';
        const useMediaSource =
          "MediaSource" in window && MediaSource.isTypeSupported(mime);

        if (useMediaSource) {
          mediaSource = new MediaSource();
          currentBlobUrl = URL.createObjectURL(mediaSource);
          setBlobUrl(currentBlobUrl);

          await new Promise<void>((resolve) => {
            mediaSource!.addEventListener("sourceopen", () => resolve(), { once: true });
          });

          let sourceBuffer: SourceBuffer;
          try {
            sourceBuffer = mediaSource.addSourceBuffer(mime);
          } catch {
            // MIME unsupported — fallback to blob assembly
            mediaSource = null;
            URL.revokeObjectURL(currentBlobUrl!);
            currentBlobUrl = null;
            await assembleAsBlob(first.buffer, totalSize);
            return;
          }

          const appendBuffer = (buf: ArrayBuffer) => new Promise<void>((res, rej) => {
            const onUpdate = () => {
              sourceBuffer.removeEventListener("updateend", onUpdate);
              sourceBuffer.removeEventListener("error", onErr);
              res();
            };
            const onErr = () => {
              sourceBuffer.removeEventListener("updateend", onUpdate);
              sourceBuffer.removeEventListener("error", onErr);
              rej(new Error("SourceBuffer error"));
            };
            sourceBuffer.addEventListener("updateend", onUpdate);
            sourceBuffer.addEventListener("error", onErr);
            try { sourceBuffer.appendBuffer(buf); } catch (e) { rej(e); }
          });

          try {
            await appendBuffer(first.buffer);
          } catch {
            // Non-fragmented MP4 — fallback
            try { mediaSource.endOfStream(); } catch {}
            URL.revokeObjectURL(currentBlobUrl!);
            currentBlobUrl = null;
            mediaSource = null;
            await assembleAsBlob(first.buffer, totalSize);
            return;
          }
          setLoading(false);

          // Stream remaining chunks
          let pos = first.buffer.byteLength;
          while (pos < totalSize && !cancelled) {
            const end = Math.min(pos + CHUNK_SIZE - 1, totalSize - 1);
            const chunk = await fetchChunk(currentToken, pos, end);
            if (cancelled) return;
            await appendBuffer(chunk.buffer);
            pos += chunk.buffer.byteLength;
          }
          if (!cancelled && mediaSource && mediaSource.readyState === "open") {
            try { mediaSource.endOfStream(); } catch {}
          }
        } else {
          await assembleAsBlob(first.buffer, totalSize);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error("[secure-stream] error:", e);
          setError(e?.message || "Failed to load video");
          setLoading(false);
        }
      }
    };

    const assembleAsBlob = async (firstChunk: ArrayBuffer, totalSize: number) => {
      const parts: BlobPart[] = [firstChunk];
      let pos = firstChunk.byteLength;
      while (pos < totalSize && !cancelled) {
        const end = Math.min(pos + CHUNK_SIZE - 1, totalSize - 1);
        const chunk = await fetchChunk(currentToken, pos, end);
        if (cancelled) return;
        parts.push(chunk.buffer);
        pos += chunk.buffer.byteLength;
      }
      if (cancelled) return;
      const blob = new Blob(parts, { type: "video/mp4" });
      currentBlobUrl = URL.createObjectURL(blob);
      setBlobUrl(currentBlobUrl);
      setLoading(false);
    };

    cleanupRef.current = () => {
      cancelled = true;
      if (tokenRefreshTimer !== null) clearInterval(tokenRefreshTimer);
      if (mediaSource && mediaSource.readyState === "open") {
        try { mediaSource.endOfStream(); } catch {}
      }
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };

    start();

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      setBlobUrl(null);
    };
  }, [source, resourceId, lessonId, enabled]);

  return { blobUrl, loading, error };
};
